import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated, getCurrentSession, type UserRole } from '../../utils/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}

const DEFAULT_REDIRECT: Record<UserRole, string> = {
  admin: '/kurier/auftraege',
  kurier: '/kurier/dashboard',
  servicecenter: '/kurier/dashboard',
};

// Welche Routen sind für welche Rollen erlaubt
const ROLE_ALLOWED_PATHS: Record<UserRole, string[]> = {
  admin: [
    '/kurier/auftraege',
    '/kurier/einsatztage',
    '/kurier/user-administration',
    '/kurier/einstellungen',
    '/kurier/seed-test-data',
    '/kurier/user-profile',
  ],
  kurier: [
    '/kurier/dashboard',
    '/kurier/user-profile',
  ],
  servicecenter: [
    '/kurier/dashboard',
    '/kurier/user-profile',
  ],
};

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const location = useLocation();

  useEffect(() => {
    setAuthenticated(isAuthenticated());
  }, []);

  if (authenticated === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-gray-400 animate-spin"></i>
          <p className="text-sm text-gray-600 mt-4">Wird geladen...</p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return <Navigate to="/kurier" replace />;
  }

  const session = getCurrentSession();
  const role = session?.role as UserRole;

  if (role) {
    const allowedPaths = ROLE_ALLOWED_PATHS[role] ?? [];
    const isAllowed = allowedPaths.some((p) => location.pathname.startsWith(p));

    if (!isAllowed) {
      return <Navigate to={DEFAULT_REDIRECT[role]} replace />;
    }
  }

  return <>{children}</>;
}
